'use client';

import { Dropdown, Grid, TextLink } from '@petsmart-ui/sparky';
import classNames from 'classnames';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef } from 'react';

import { trackNavigationHeaderClick } from '@libs/analytics';
import { AccountDrawerType, getENV, useCart, useDecisions, useLogout, usePageLayout } from '@libs/common-next';
import {
    getPathnameFromPath,
    isLastActiveExpired,
    isRestrictedPathname,
    isUserLoggedIn,
    NAVIGATION_URLS,
} from '@libs/core';
import { handleAccountClick } from '@libs/experience-api';

import { useAccountDropdown } from '../../use-account-dropdown';

import './LoggedIn.scss';

const navigationUrls = NAVIGATION_URLS.velocity.account;

const navigationItems = [
    { label: 'About you', trackingKey: 'about you', url: navigationUrls.aboutYou },
    { label: 'Credit cards', trackingKey: 'payments', url: navigationUrls.creditCards },
    { label: 'Appointments', trackingKey: 'appointments', url: navigationUrls.servicesGrooming },
    { label: 'Autoship', trackingKey: 'autoship', url: navigationUrls.autoShip },
    { label: 'Recent orders', trackingKey: 'orders', url: navigationUrls.orderHistory },
    { label: 'Offers', trackingKey: 'offers', url: navigationUrls.offers },
];

export function LoggedIn() {
    const { firstName, loyaltyPointsText } = useAccountDropdown();
    const { openAccountDrawer, setSignInDrawerProps } = usePageLayout();
    const { soft_login } = useDecisions();
    const { clearCart } = useCart();
    const logout = useLogout();
    const router = useRouter();

    const isLoyaltyAccountMenu = getENV('NEXT_PUBLIC_LOYALTY_ACCOUNT_MENU');
    const link = useRef<string>('');

    const wrapperClasses = useMemo(
        () => classNames('legacy-loggedin', isLoyaltyAccountMenu ? 'loyalty-account-menu-enabled' : ''),
        [isLoyaltyAccountMenu]
    );

    useEffect(() => {
        if (globalThis.window !== undefined) {
            link.current = globalThis.location.origin;
        }
    }, []);

    const handleLinkClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        const link = event.currentTarget.href;
        const rawLinkLabel =
            event.currentTarget.dataset.linkLabel ??
            (event.currentTarget.innerText ?? event.currentTarget.textContent ?? '').trim();
        const linkLabel = rawLinkLabel && rawLinkLabel.length > 0 ? rawLinkLabel : 'Account';

        const shouldOpenRestrictedLinkInSignInDrawer = soft_login && isUserLoggedIn() && isLastActiveExpired();

        if (shouldOpenRestrictedLinkInSignInDrawer && isRestrictedPathname(getPathnameFromPath(link))) {
            await handleAccountClick({ event, link, skipNavigation: true, skipRedirect: true, router });

            if (isUserLoggedIn()) {
                return;
            }

            setSignInDrawerProps({
                redirectUrl: link,
                signInTitle: `${linkLabel} sign in`,
            });
            openAccountDrawer(AccountDrawerType.SIGN_IN);
            return;
        }

        await handleAccountClick({ event, link, router });
    };

    if (!firstName) {
        return null;
    }

    return (
        <div className={wrapperClasses} data-testid="logged-in">
            <div className="legacy-loggedin__button">
                <TextLink
                    href={navigationUrls.treatOffers}
                    data-link-label={isLoyaltyAccountMenu ? 'Treats Rewards' : 'Account'}
                    onClick={async event => {
                        await handleLinkClick(event as React.MouseEvent<HTMLAnchorElement>);
                    }}
                >
                    <span className="legacy-loggedin__greetings">
                        Hi,{' '}
                        <span className="legacy-loggedin__user-name" data-cs-mask>
                            {firstName}
                        </span>
                    </span>
                    <span className="legacy-account__caret" />
                </TextLink>
            </div>

            <div className="legacy-loggedin__dropdown-wrapper">
                <Dropdown className="legacy-loggedin__dropdown">
                    <div className="legacy-loggedin__submenu">
                        <a
                            className="points-link"
                            href={navigationUrls.treatOffers}
                            data-link-label={isLoyaltyAccountMenu ? 'Treats Rewards' : 'Account'}
                            onClick={async event => {
                                await handleLinkClick(event);
                            }}
                            title="Go to: View your rewards and offers"
                        >
                            <div className="points">
                                <span className="point-count" data-cs-mask>{` ${loyaltyPointsText} `}</span>
                                {`pts. `}
                                <span className="account">{isLoyaltyAccountMenu ? 'Treats Rewards' : 'Account'}</span>
                            </div>
                        </a>

                        <Grid variant="1-2up" gap="none">
                            {navigationItems.map(item => (
                                <Grid.Item key={item.trackingKey}>
                                    <TextLink
                                        href={item.url}
                                        data-link-label={item.label}
                                        onClick={async event => {
                                            trackNavigationHeaderClick(item.trackingKey, link.current + item.url);
                                            handleLinkClick(event as React.MouseEvent<HTMLAnchorElement>);
                                        }}
                                    >
                                        {item.label}
                                    </TextLink>
                                </Grid.Item>
                            ))}
                        </Grid>
                        <TextLink
                            className="logout-link"
                            onClick={async () =>
                                await logout({ redirectUrl: '/sign-in', forceRedirect: true, callback: clearCart })
                            }
                        >
                            Logout
                        </TextLink>
                    </div>
                </Dropdown>
            </div>

            <div className="dropdown-accent" aria-hidden />
        </div>
    );
}
